import * as React from "react";
import { useState } from "react";
import { vorgangService, type VorgangPickerItem } from "../../../services/vorgangService";
import VorgangPickerAuswahl from "../picker/VorgangPickerAuswahl";
import { IcoX } from "../v3/vg3Icons";
// `VorgangPickerAuswahl` holt seine Optik aus `.cal-vgd-*` in calendar.css
// und importiert sie NICHT selbst — ohne diese Zeile steht die Liste nackt da.
import "../../calendar/calendar.css";
import "../v3/vorgangV3.css";

type Vorgang = VorgangPickerItem["vorgang"];

/**
 * „Diesen Vorgang als To-do verschieben…" (PO 21.09.2026).
 *
 * ══ WOZU ═══════════════════════════════════════════════════════════════════
 * > „das ich ein vorgang ohne todo als todo hierrein verschieben kann."
 *
 * Ein eigener Vorgang war zu groß gedacht — die Sache ist ein Arbeitsschritt
 * in einem anderen Vorgang. Bisher blieb nur: drüben ein To-do neu tippen und
 * den Schriftwechsel von Hand nachziehen.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ EINSTUFIG, ANDERS ALS DIE NACHBARN ═════════════════════════════════════
 * `TodoVerschiebenDialog` und `TodoVerknuepfenDialog` fragen zweimal: aus
 * welchem Vorgang, dann welches To-do. Hier steht das Bewegte schon fest — es
 * ist der Vorgang, in dem man gerade ist. Zu fragen bleibt nur das Ziel.
 *
 * ══ WARUM EINE RÜCKFRAGE ═══════════════════════════════════════════════════
 * Der Schritt ist nicht bequem umkehrbar: Der Vorgang landet im Papierkorb,
 * und beim Wiederherstellen kommt sein Inhalt NICHT mit zurück — der hängt
 * dann am neuen To-do. Dieselbe Eigenschaft hat das Zusammenführen, und dort
 * sagt der Dialog es ebenfalls vorher.
 */
type Props = {
    /** Der Vorgang, der zum To-do wird — die QUELLE. */
    vorgangId: number;
    /** Sein Betreff, für die Rückfrage. */
    titel: string;
    contactId?: number | null;
    onClose: () => void;
    /** Verschoben — die Seite muss den Vorgang verlassen, er ist gelöscht. */
    onVerschoben: (zielVorgangId: number) => void | Promise<void>;
};

export const VorgangAlsTodoVerschiebenDialog: React.FC<Props> = ({
    vorgangId,
    titel,
    contactId = null,
    onClose,
    onVerschoben,
}) => {
    const [ziel, setZiel] = useState<Vorgang | null>(null);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const verschiebe = async () => {
        if (busy || ziel == null) return;
        setBusy(true);
        setFehler(null);
        try {
            await vorgangService.verschiebeVorgangAlsTodo(vorgangId, ziel.id);
            await onVerschoben(ziel.id);
            onClose();
        } catch (err: any) {
            console.error("[VorgangAlsTodo] Verschieben fehlgeschlagen", err);
            // Der Server lehnt bewusst ab, wenn der Vorgang doch To-dos hat
            // (400) oder das Schreibrecht fehlt (403). Beides steht in `message`
            // und ist für den Anwender die eigentliche Auskunft.
            setFehler(err?.message ?? "Verschieben fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="kvp-rd vg3-modal-hg" role="presentation" onClick={onClose}>
            <div
                className="vg3-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Diesen Vorgang als To-do in einen anderen Vorgang verschieben"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="vg3-modal-kopf">
                    <span className="vg3-eyebrow">Vorgang als To-do verschieben</span>
                    <button
                        type="button"
                        className="vg3-ibtn"
                        title="Schließen"
                        aria-label="Schließen"
                        onClick={onClose}
                    >
                        <IcoX size={16} />
                    </button>
                </div>

                {fehler && (
                    <div className="vg3-fehler" role="alert">
                        {fehler}
                    </div>
                )}

                <div className="vg3-modal-b">
                    {ziel == null ? (
                        <>
                            <p className="vg3-modal-hinweis">
                                In welchen Vorgang? „{titel || "(ohne Betreff)"}“ wird dort ein To-do —
                                mit Notizen, Mails, Anrufen, Terminen und Dokumenten.
                            </p>
                            <VorgangPickerAuswahl
                                onPick={setZiel}
                                contactId={contactId}
                                aktuelleVorgangId={vorgangId}
                                disabled={busy}
                            />
                        </>
                    ) : (
                        <>
                            <div className="vg3-modal-quelle">
                                <span className="vg3-modal-quelle-t">
                                    {ziel.vorgangNbr ? `${ziel.vorgangNbr} · ` : ""}
                                    {ziel.titel}
                                </span>
                                <button
                                    type="button"
                                    className="vg3-tbtn"
                                    disabled={busy}
                                    onClick={() => setZiel(null)}
                                >
                                    anderer Vorgang
                                </button>
                            </div>

                            <p className="vg3-modal-hinweis">
                                Dieser Vorgang wird danach in den Papierkorb gelegt. Er lässt sich von
                                dort zurückholen — sein Inhalt kommt dabei aber nicht mit, der bleibt
                                am neuen To-do.
                            </p>

                            <div className="vg3-modal-fuss">
                                <button
                                    type="button"
                                    className="vg3-btn"
                                    disabled={busy}
                                    onClick={onClose}
                                >
                                    Abbrechen
                                </button>
                                <button
                                    type="button"
                                    className="vg3-btn primary"
                                    disabled={busy}
                                    onClick={() => void verschiebe()}
                                >
                                    {busy ? "Wird verschoben…" : "Verschieben"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VorgangAlsTodoVerschiebenDialog;
